import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Badge from '@/components/base/Badge';
import DataTable, { type Column } from '@/components/base/DataTable';
import { formatCurrency, formatNumber } from '@/lib/format';
import type { OrderFinanceView } from '@/types';

interface CommissionsTableProps {
  details: OrderFinanceView[];
  currency: string;
}

function percent(value: number): string {
  return `${formatNumber(value * 100, 0)}%`;
}

function ViewOrderLink({ id }: { id: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/dashboard/orders/${id}`}
      className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
    >
      <i className="ri-eye-line text-base" aria-hidden="true" />
      {t('finance.details.viewOrder')}
    </Link>
  );
}

function DebtBadge() {
  const { t } = useTranslation();
  return (
    <Badge variant="warning" icon="ri-error-warning-line">
      {t('finance.details.debtBadge')}
    </Badge>
  );
}

export default function CommissionsTable({ details, currency }: CommissionsTableProps) {
  const { t } = useTranslation();

  const columns: Column<OrderFinanceView>[] = [
    {
      key: 'order',
      header: t('finance.details.col.order'),
      render: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="lb-ltr-field font-semibold text-foreground-950">{row.orderId}</span>
          {row.customerDebt > 0 ? <DebtBadge /> : null}
        </div>
      ),
    },
    {
      key: 'finalTotal',
      header: t('finance.details.col.finalTotal'),
      render: (row) => (
        <span className="whitespace-nowrap text-foreground-800">
          {formatCurrency(row.finalTotal, currency)}
        </span>
      ),
    },
    {
      key: 'commissionRate',
      header: t('finance.details.col.commissionRate'),
      render: (row) => <span className="lb-ltr-field text-foreground-700">{percent(row.commissionRate)}</span>,
    },
    {
      key: 'commissionAmount',
      header: t('finance.details.col.commissionAmount'),
      render: (row) => (
        <span className="whitespace-nowrap text-foreground-800">
          {formatCurrency(row.commissionAmount, currency)}
        </span>
      ),
    },
    {
      key: 'deliveryFee',
      header: t('finance.details.col.deliveryFee'),
      render: (row) =>
        row.deliveryFeeToLaundry > 0 ? (
          <span className="whitespace-nowrap text-foreground-800">
            {formatCurrency(row.deliveryFeeToLaundry, currency)}
          </span>
        ) : (
          <span className="text-foreground-400">{t('finance.none')}</span>
        ),
    },
    {
      key: 'net',
      header: t('finance.details.col.net'),
      render: (row) => (
        <span className="whitespace-nowrap font-semibold text-foreground-950">
          {formatCurrency(row.netPayable, currency)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-end',
      render: (row) => (
        <div className="flex justify-end">
          <ViewOrderLink id={row.orderId} />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="hidden md:block">
        <DataTable columns={columns} rows={details} rowKey={(row) => row.orderId} />
      </div>

      <ul className="divide-y divide-background-100 md:hidden">
        {details.map((row) => (
          <li key={row.orderId} className="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="lb-ltr-field text-sm font-semibold text-foreground-950">{row.orderId}</span>
              {row.customerDebt > 0 ? <DebtBadge /> : null}
            </div>

            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.details.col.finalTotal')}</dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground-900">
                  {formatCurrency(row.finalTotal, currency)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.details.col.commissionRate')}</dt>
                <dd className="lb-ltr-field mt-0.5 text-sm font-medium text-foreground-900">
                  {percent(row.commissionRate)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.details.col.commissionAmount')}</dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground-900">
                  {formatCurrency(row.commissionAmount, currency)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.details.col.deliveryFee')}</dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground-900">
                  {row.deliveryFeeToLaundry > 0
                    ? formatCurrency(row.deliveryFeeToLaundry, currency)
                    : t('finance.none')}
                </dd>
              </div>
            </dl>

            <div className="flex items-center justify-between gap-3 border-t border-background-100 pt-3">
              <div>
                <p className="text-xs text-foreground-500">{t('finance.details.col.net')}</p>
                <p className="mt-0.5 text-sm font-bold text-foreground-950">
                  {formatCurrency(row.netPayable, currency)}
                </p>
              </div>
              <ViewOrderLink id={row.orderId} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}